import { useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

export function NotificationSettingsDialog({ open, onOpenChange }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/me/notification-settings")
      .then(({ data }) => setGroups(data.groups))
      .catch((error) => toast.error(errMsg(error)))
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = async (group) => {
    const enabled = !group.enabled;
    setSavingKey(group.key);
    setGroups((current) => current.map((item) => (
      item.key === group.key ? { ...item, enabled } : item
    )));
    try {
      const { data } = await api.put("/me/notification-settings", {
        settings: { [group.key]: enabled },
      });
      setGroups(data.groups);
    } catch (error) {
      setGroups((current) => current.map((item) => (
        item.key === group.key ? { ...item, enabled: group.enabled } : item
      )));
      toast.error(errMsg(error));
    } finally {
      setSavingKey("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="notification-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-primary" />
            Notification settings
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="space-y-3 py-3" data-testid="notification-settings-loading">
            <div className="h-14 animate-pulse rounded-lg bg-muted" />
            <div className="h-14 animate-pulse rounded-lg bg-muted" />
          </div>
        ) : (
          <div className="divide-y">
            {groups.map((group) => (
              <div key={group.key} className="flex items-center gap-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{group.label}</p>
                  <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                    {group.description}
                  </p>
                </div>
                <Switch
                  checked={group.enabled}
                  disabled={savingKey === group.key}
                  onCheckedChange={() => toggle(group)}
                  aria-label={`Toggle ${group.label}`}
                  data-testid={`notification-setting-${group.key}`}
                />
              </div>
            ))}
            {groups.length === 0 && (
              <p className="py-5 text-sm text-muted-foreground">
                No notification settings are available for this account.
              </p>
            )}
          </div>
        )}
        <div className="flex justify-end pt-1">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="notification-settings-close-button"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}